import React, { useState, useEffect } from 'react';
import { EnvelopeIcon } from '@heroicons/react/24/outline';
import Modal from '../Common/Modal';
import { cvScreeningAPI } from '../../api/client';
import toast from 'react-hot-toast';

// Modal for sending interview-invite emails to shortlisted candidates.
// `candidates` is an array of { name, position } extracted from the AI chat
// markdown screening response. For each candidate we attempt to auto-fill the
// email address by scanning the project's CV documents, then let the user edit
// before sending via the existing send-manual-interview-email endpoint.
export default function SendInterviewEmailModal({ open, onClose, candidates, projectId, onSent }) {
  const [rows, setRows] = useState([]);
  const [sending, setSending] = useState(false);
  const [sendSchedule, setSendSchedule] = useState(false);

  useEffect(() => {
    if (!open || candidates.length === 0) return;

    const initial = candidates.map((c) => ({
      name: c.name,
      email: '',
      position_title: c.position || '',
      fetching: true,
      selected: true,
    }));
    setRows(initial);

    candidates.forEach((c, i) => {
      cvScreeningAPI
        .extractEmail(projectId, c.name)
        .then((res) => {
          setRows((prev) =>
            prev.map((r, j) =>
              j === i ? { ...r, email: res.data.email || '', fetching: false } : r
            )
          );
        })
        .catch(() => {
          setRows((prev) =>
            prev.map((r, j) => (j === i ? { ...r, fetching: false } : r))
          );
        });
    });
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const setField = (i, field, value) =>
    setRows((prev) => prev.map((r, j) => (j === i ? { ...r, [field]: value } : r)));

  const selectedRows = rows.filter((r) => r.selected);
  const allSelected = rows.length > 0 && selectedRows.length === rows.length;

  const toggleAll = () =>
    setRows((prev) => prev.map((r) => ({ ...r, selected: !allSelected })));

  const handleSend = async () => {
    setSending(true);
    try {
      const res = await cvScreeningAPI.sendManualInterviewEmail({
        candidates: selectedRows.map((r) => ({
          name: r.name,
          email: r.email,
          position_title: r.position_title,
        })),
        project_id: projectId,
        send_schedule: sendSchedule,
      });
      const { sent = [], failed = [], skipped = [] } = res.data;
      if (sent.length > 0) {
        const noun = sendSchedule ? 'scheduling link' : 'interview invite';
        toast.success(`${sent.length} ${noun}${sent.length !== 1 ? 's' : ''} sent`);
      }
      if (skipped.length > 0)
        toast(`${skipped.length} already invited — skipped`, { icon: 'ℹ️' });
      if (failed.length > 0)
        toast.error(`${failed.length} failed to send`);
      onSent();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send emails');
    } finally {
      setSending(false);
    }
  };

  const stillFetching = rows.some((r) => r.fetching);
  const noneSelected = selectedRows.length === 0;
  // `|| ''` because this is only safe today by how THIS component happens to
  // build its rows (email starts '' and the lookup coalesces). candidates.email
  // is nullable, so the moment anyone feeds candidate records in directly this
  // throws on the first row with no address.
  const noEmails = selectedRows.every((r) => !(r.email || '').trim());

  return (
    <Modal open={open} onClose={onClose} title="Send Interview Email" size="lg">
      <div className="flex flex-col gap-4">
        <p className="text-sm text-gray-500">
          Review and edit each candidate's details before sending the interview invite.
        </p>
        <div className="flex items-center justify-between">
          <span className="text-xs font-medium text-gray-500">
            {selectedRows.length} of {rows.length} selected
          </span>
          <button
            type="button"
            onClick={toggleAll}
            disabled={rows.length === 0}
            className="text-xs font-medium text-primary-600 hover:text-primary-700 disabled:opacity-50"
          >
            {allSelected ? 'Deselect All' : 'Select All'}
          </button>
        </div>
        <div className="flex flex-col gap-3 max-h-[50vh] overflow-y-auto pr-1">
          {rows.map((row, i) => (
            <div
              key={i}
              className={`rounded-xl border p-4 flex flex-col gap-2 transition-colors ${
                row.selected ? 'border-gray-200' : 'border-gray-200 bg-gray-50 opacity-60'
              }`}
            >
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={row.selected}
                  onChange={(e) => setField(i, 'selected', e.target.checked)}
                  className="w-4 h-4 cursor-pointer accent-primary-600"
                />
                <span className="text-sm font-semibold text-gray-800">{row.name}</span>
              </label>
              <div className="flex gap-2">
                <div className="flex-1">
                  <label className="text-xs font-medium text-gray-500">Email</label>
                  <input
                    type="email"
                    value={row.email}
                    onChange={(e) => setField(i, 'email', e.target.value)}
                    placeholder={row.fetching ? 'Searching CV…' : 'Enter email address'}
                    disabled={row.fetching}
                    className="mt-0.5 w-full rounded-lg border border-gray-200 px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-300 disabled:bg-gray-50 disabled:text-gray-400"
                  />
                </div>
                <div className="flex-1">
                  <label className="text-xs font-medium text-gray-500">Position</label>
                  <input
                    type="text"
                    value={row.position_title}
                    onChange={(e) => setField(i, 'position_title', e.target.value)}
                    placeholder="Position title"
                    className="mt-0.5 w-full rounded-lg border border-gray-200 px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary-300"
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
        <label className="flex items-start gap-2 cursor-pointer rounded-xl border border-gray-200 p-3 hover:bg-gray-50 transition-colors">
          <input
            type="checkbox"
            checked={sendSchedule}
            onChange={(e) => setSendSchedule(e.target.checked)}
            className="mt-0.5 w-4 h-4 cursor-pointer accent-primary-600"
          />
          <span className="text-sm text-gray-700">
            Send interview scheduling link directly
            <span className="block text-xs text-gray-400">
              Skips the information form and emails the scheduling link straight away.
            </span>
          </span>
        </label>

        <div className="flex justify-end gap-2 pt-2 border-t border-gray-100">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800 rounded-xl hover:bg-gray-100 transition-colors"
          >
            Cancel
          </button>
          <button
            onClick={handleSend}
            disabled={sending || stillFetching || noneSelected || noEmails}
            className="flex items-center gap-2 px-4 py-2 text-sm bg-primary-600 text-white rounded-xl hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <EnvelopeIcon className="w-4 h-4" />
            {sending
              ? 'Sending…'
              : stillFetching
              ? 'Loading…'
              : noneSelected
              ? 'Select at least one'
              : sendSchedule
              ? `Send ${selectedRows.length} Schedule Link${selectedRows.length !== 1 ? 's' : ''}`
              : `Send ${selectedRows.length} Invite${selectedRows.length !== 1 ? 's' : ''}`}
          </button>
        </div>
      </div>
    </Modal>
  );
}
